
import { Add, Delete, Edit, RadioRounded } from '@mui/icons-material';
import BlindsClosedIcon from '@mui/icons-material/BlindsClosed';
import { Skeleton, Stack, Switch, FormControlLabel, Typography, TextareaAutosize, TextField, CircularProgress } from '@mui/material';
import EmptyContent from 'components/EmptyContent';
import List from 'components/UI/List';
import { useAlert } from 'contexts/alertContext';
import useApp from 'hooks/useApp';
import { Feature } from 'models/api';
import { IWorkshopsGroup, IWorkshopsGroupInput } from 'models/workshopsgroup';
import React, { useCallback, useEffect, useState } from 'react';
import { QueryClient, useMutation } from 'react-query';

import { getInvitationDetails, respondToInvitation } from 'framework/workshopInvitations';
import { handleError } from 'helpers/utils';
import { IWorkshop } from 'models/workshop';
import { getWorkshops } from 'framework/workshops';
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router-dom';
import { IWorkshopInvitationResponse, IWorkshopInvitationResponseInput } from 'models/workshopInvitations';
import RHSwitch from 'components/UI/FormFields/RHSwitch';
import { IOption } from 'models/app';
import { useForm } from 'react-hook-form';
import AcceptRejectRadio from 'components/CustomRadioButton';
import MuiButton from 'components/UI/MuiButton';
import { GetByInvitationGroupAndEmployee, getByWorkshopAndEmployee, getEnrollment, withdraw } from 'framework/enrollment';
import { IEnrollment } from 'models/enrollment';
import CustomRadioButton from 'components/CustomRadioButton';

interface Props {
	feature: Feature;
}

const WorkshopInvitationResponse: React.FC<Props> = ({ feature }) => {
	const urlParams = useParams();
	let id: string = urlParams[`${feature.singleName}Id`] as string;
	const [loading, setLoading] = useState<boolean>(false);
	const { t, i18n } = useTranslation();
	const [workshopDetails, setWorkshopDetails] = useState<IWorkshopInvitationResponse>({} as IWorkshopInvitationResponse);
	const { setAlert } = useAlert();
	const [radioValue, setRadioValue] = useState('accept');
	const [workshopRadioValue, setWorkshopRadioValue] = useState('');
	const [showRejectionReason, setShowRejectionReason] = useState(false);
	const [rejectionReasonValue, setRejectionReasonValue] = useState('');
	const [enrollmentStatus, setEnrollmentStatus] = useState('');
	const [isGroupWorkshop, setIsGroupWorkshop] = useState(false);
	const [workshopsInGroupOptions, setWorkshopsInGroupOptions] = useState<Option[]>([] as Option[]);
	const [enrollmentDetails, setEnrollmentDetails] = useState<IEnrollment>({} as IEnrollment);
	const [alreadyChosenWorkshop, setAlreadyChosenWorkshop] = useState(false);
	const [workshopsInGroup, setWorkshopsInGroup] = useState<IWorkshop[]>([] as IWorkshop[]);

	interface Option {
		value: string;
		label: string;
	}
	const acceptOrRejectoptions = [
		{ value: 'accept', label: 'Accept' },
		{ value: 'reject', label: 'Reject' }
	];

	const handleRejectionReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRejectionReasonValue(event.target.value); // Update the state with the new value
	};

	const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRadioValue(event.target.value);
		setShowRejectionReason(event.target.value == 'reject');
	};

	const handleWorkshopRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setWorkshopRadioValue(event.target.value);
	};

	// eslint-disable-next-line
	const queryClient = new QueryClient();

	const respondToInvitationMutation = useMutation({
		mutationFn: (createInput: IWorkshopInvitationResponseInput) => {
			return respondToInvitation(createInput, id);
		},
	});

	const withdrawMutation = useMutation({
		mutationFn: (id: string) => {
			return withdraw(id);
		},
	});

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			let response: any = await queryClient.fetchQuery('get invitation details', () => getInvitationDetails(id));
			setWorkshopDetails(response);
			if (response.workshopsGroupId) {
				setIsGroupWorkshop(true);
				let workshopsInGroupResponse: any = await queryClient.fetchQuery('get workshops details', () => getWorkshops('/group?groupId=' + response.workshopsGroupId));
				const workshopsInGroupOptions = workshopsInGroupResponse.map((workshop: any) => ({
					value: workshop.workshopId,
					label: workshop.topicName + ' | ' + workshop.venueName + ' | ' + workshop.fromDate + ' - ' + workshop.toDate + ' (Available Places: ' + (workshop.capacity - workshop.enrolled) + ')'
				}));
				setWorkshopsInGroup(workshopsInGroupResponse);
				setWorkshopsInGroupOptions(workshopsInGroupOptions);
			}
			if (response.status == 1) {
				if (response.workshopsGroupId) {
					let response2: any = await queryClient.fetchQuery('get enrollment details', () => GetByInvitationGroupAndEmployee(response.id, response.employeeId));
					setEnrollmentDetails(response2);
					setWorkshopRadioValue(response2.workshopId)
					setAlreadyChosenWorkshop(true);
				}
				else {
					let response2: any = await queryClient.fetchQuery('get enrollment details', () => getByWorkshopAndEmployee(response.workshopId, response.employeeId));
					setEnrollmentDetails(response2);
				}
			}
			setLoading(false);
		}
		catch (error: any) {
			setAlert({
				open: true,
				message: handleError(error) || 'Something went wrong',
				type: 'error'
			});
			setLoading(false);
		}

		// eslint-disable-next-line
	}, [queryClient, setAlert]);

	useEffect(() => {
		fetchData();
		// eslint-disable-next-line
	}, []);


	if (loading) return <Stack spacing={2} p={2}>
		{Array.from({ length: 10 }).map((_, i) => (
			<Skeleton variant="rectangular" key={i} width="100%" height={30} />
		))}
	</Stack>;

	const handleSubmit = async () => {
		if (
			(radioValue == 'reject' && rejectionReasonValue == '') || (isGroupWorkshop && workshopRadioValue == '')
		)
			return setAlert({
				open: true,
				message: "Please fill all required fields",
				type: "error",
			});
		try {
			setLoading(true);

			const createInput: IWorkshopInvitationResponseInput = {
				id: id,
				status: radioValue == 'accept' ? 1 : 2,
				rejectionReason: rejectionReasonValue,
				//workshopId: isGroupWorkshop ? workshopRadioValue : ''
			};
			await respondToInvitationMutation.mutateAsync(createInput);
			setAlert({
				open: true,
				message: "Employee added successfully",
				type: "success",
			});
			fetchData();
			setLoading(false);
		} catch (error: any) {
			setAlert({
				open: true,
				message: handleError(error) || "Something went wrong",
				type: "error",
			});
			setLoading(false);
		}
	};

	const handleWithdraw = async () => {
		await withdrawMutation.mutateAsync(enrollmentDetails.workshopId);
		setAlert({
			open: true,
			message: "You have been successfuly withdrawn from the workshop",
			type: "success",
		});
		fetchData();
		setLoading(false);
	}



	return (
		<>
			<Stack spacing={2} p={2}>
				<div>
					<div>

						{t("T_hello")} {workshopDetails?.employeeName + ', '}
						{workshopDetails.status == 0 ? isGroupWorkshop ? 'You have been invited to register in one of the below workshops' : 'you have been invited to register in a workshop with the following details:' :
							workshopDetails.status == 1 ? enrollmentDetails.status == 1 ? isGroupWorkshop ? '' : 'you are enrolled in this workshop.' : 'you have widthdrawn from this workshop' : 'you have rejected to register int this workshop'
						}
						,
					</div>
					{!isGroupWorkshop && (
						<>
							{workshopDetails.capacity == workshopDetails.enrolled && (<div>There are no availbale places in this workshop, if you accept the invitation, you will be added to the waiting list.</div>)}
							<div>
								<span style={{ fontWeight: "bold" }}>{t("T_title")}:</span> {workshopDetails?.workshopTitle}
							</div>
							<div>
								<span style={{ fontWeight: "bold" }}>Date:</span> {workshopDetails?.fromDate} - {workshopDetails?.toDate}
							</div>
							<div>
								<span style={{ fontWeight: "bold" }}>Venue:</span> {workshopDetails?.venue}
							</div>
							<div>
								<span style={{ fontWeight: "bold" }}>Available Places:</span> {workshopDetails?.capacity - workshopDetails?.enrolled}
							</div>
						</>)
					}

				</div>

				{workshopDetails.status === 0 && (
					<>
						<div>
							<CustomRadioButton disabled={false} title='Decision' options={acceptOrRejectoptions} value={radioValue} onChange={handleRadioChange} />
						</div>
						{showRejectionReason && (
							<div>
								<span style={{ fontWeight: "bold" }}>{t("T_rejection_reason")}:</span>
								<TextField onChange={handleRejectionReasonChange} name="rejectionReason" />
							</div>
						)}
					</>
				)}

				{isGroupWorkshop && radioValue === 'accept' && (
					<>
						<div>
							<CustomRadioButton disabled={alreadyChosenWorkshop} title={alreadyChosenWorkshop ? enrollmentDetails.status == 2 ? '' : 'You have already chosen a workshop' : 'Choose a wokrshop'} options={workshopsInGroupOptions} value={workshopRadioValue} onChange={handleWorkshopRadioChange} />
						</div>
						{showRejectionReason && (
							<div>
								<span style={{ fontWeight: "bold" }}>{t("T_rejection_reason")}:</span>
								<TextField onChange={handleRejectionReasonChange} name="rejectionReason" />
							</div>
						)}

					</>
				)}

				{workshopDetails.status == 0 && (
					<MuiButton
						variant="contained"
						sx={{ bgcolor: "primary.main", color: "white" }}
						disabled={loading}
						size="large"
						onClick={handleSubmit}
					>
						{t("T_submit")}
						<CircularProgress size={10} sx={{ mx: 2, display: loading ? "" : "none" }} />
					</MuiButton>)}
			</Stack>

			{enrollmentDetails.status === 1 && (
				<MuiButton
					variant="contained"
					sx={{ bgcolor: "primary.main", color: "white" }}
					disabled={loading}
					size="large"
					onClick={handleWithdraw}
				>
					{t("T_withdraw")}
					<CircularProgress size={10} sx={{ mx: 2, display: loading ? "" : "none" }} />
				</MuiButton>
			)}
		</>
	);



}

export default WorkshopInvitationResponse;